"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTextEditorHandler = void 0;
const editor_utils_1 = require("./editor-utils");
// NOTE: There seems to a bug in chaining commands that are manipulating the same element/attribute: https://github.com/ueberdosis/tiptap/issues/5792
// Until this is resolved, make sure to call these commands separately rather than chaining them (see subscript toggle for an example)
function createTextEditorHandler(editor) {
    return {
        updateShouldTargetChild: (detailMode) => {
            return (0, editor_utils_1.editorUpdateShouldTargetChild)(editor, detailMode);
        },
        focus: {
            isFocused: () => editor?.isFocused || false,
            keep: () => editor?.chain().focus().run(),
            end: () => editor?.commands.focus('end'),
        },
        blur: () => editor?.commands.blur(),
        bold: {
            set: () => editor?.chain().focus().setBold().run(),
            unset: () => editor?.chain().focus().unsetBold().run(),
            toggle: () => {
                editor?.chain().focus().toggleBold().run();
            },
            get: () => editor?.isActive('bold') || false,
        },
        fontWeight: {
            set: (value) => {
                if (value === 'regular') {
                    editor?.chain().focus().unsetBold().run();
                }
                if (value === 'bold') {
                    editor?.chain().focus().setBold().run();
                }
            },
        },
        fontFamily: {
            set: (value) => {
                editor?.chain().focus().setFontFamily(value).run();
            },
            unset: () => {
                editor?.chain().focus().unsetFontFamily().run();
            },
            unsetAll: () => {
                editor?.chain().selectAll().unsetFontFamily().blur().run();
            },
            get: (mixedValue = '-') => {
                const fontFamily = editor?.getAttributes('textStyle').fontFamily;
                // No value mean parent node value
                if (!fontFamily) {
                    return '';
                }
                if (editor?.isActive('textStyle', { fontFamily })) {
                    return fontFamily;
                }
                return mixedValue;
            },
        },
        color: {
            set: (value) => {
                editor?.chain().focus().setColor(value).run();
            },
            unset: () => {
                editor?.chain().focus().unsetColor().run();
            },
            unsetAll: () => {
                editor?.chain().selectAll().unsetColor().run();
            },
            get: () => {
                const color = editor?.getAttributes('textStyle').color;
                if (editor?.isActive('textStyle', { color })) {
                    return color;
                }
                return color;
            },
        },
        fontSize: {
            set: (value) => {
                editor?.chain().focus().setFontSize(value).run();
            },
            unset: () => {
                editor?.chain().focus().unsetFontSize().run();
            },
            unsetAll: () => {
                editor?.chain().selectAll().unsetFontSize().run();
            },
            // we always want to show a value for the font size picker,
            // otherwise the UX is confusing
            // so we can not just return a mixed value marker
            get: () => {
                return (0, editor_utils_1.hasSelection)(editor)
                    ? (0, editor_utils_1.markUnderCursorForType)(editor, 'textStyle')?.attrs.fontSize
                    : editor?.getAttributes('textStyle').fontSize;
            },
        },
        italic: {
            set: () => editor?.chain().focus().setItalic().run(),
            unset: () => {
                editor?.chain().focus().unsetItalic().run();
            },
            toggle: () => {
                editor?.chain().focus().toggleItalic().run();
            },
            get: () => editor?.isActive('italic') || false,
        },
        bulletList: {
            toggle: () => {
                editor?.chain().focus().toggleBulletList().run();
            },
            get: () => editor?.isActive('customBulletList') || false,
        },
        orderedList: {
            toggle: () => {
                editor?.chain().focus().toggleOrderedList().run();
            },
            get: () => editor?.isActive('customOrderedList') || false,
        },
        shadow: {
            set: (value) => {
                editor?.chain().focus().setShadow(value).run();
            },
            unset: () => {
                editor?.chain().focus().unsetShadow().run();
            },
            unsetAll: () => {
                editor?.chain().selectAll().unsetShadow().run();
            },
            get: () => {
                const shadow = editor?.getAttributes('textStyle').shadow;
                if (editor?.isActive('textStyle', { shadow })) {
                    return shadow;
                }
                return shadow;
            },
        },
        strikethrough: {
            toggle: () => {
                editor?.chain().focus().toggleStrike().run();
            },
            get: () => editor?.isActive('strike') || false,
        },
        underline: {
            toggle: () => {
                editor?.chain().focus().toggleUnderline().run();
            },
            get: () => editor?.isActive('underline') || false,
        },
        subscript: {
            toggle: () => {
                // TODO: Chain these commands once https://github.com/ueberdosis/tiptap/issues/5792 gets resolved
                editor?.chain().focus().toggleSubscript().run();
                editor?.commands.unsetSuperscript();
            },
            get: () => editor?.isActive('subscript') || false,
        },
        superscript: {
            toggle: () => {
                // TODO: Chain these commands once https://github.com/ueberdosis/tiptap/issues/5792 gets resolved
                editor?.chain().focus().toggleSuperscript().run();
                editor?.commands.unsetSubscript();
            },
            get: () => editor?.isActive('superscript') || false,
        },
        link: {
            set: (value) => {
                editor?.chain().focus().setLink(value).run();
            },
            unset: () => {
                editor?.chain().focus().unsetLink().run();
            },
            unsetAll: () => {
                editor?.chain().selectAll().unsetLink().run();
            },
            get: () => {
                return editor?.getAttributes('link').href;
            },
        },
    };
}
exports.createTextEditorHandler = createTextEditorHandler;
//# sourceMappingURL=text-editor-handler.js.map